// Pure data → board helpers for the phones heatmap. React-free so they
// stay unit-testable without rendering. The board is a FIXED grid: the
// whole fleet is shown, every phone keeps a slot (sorted by name), and
// cards are sized to fill the viewport — colour encodes state.

import type { HeatmapPhone, HeatmapReason } from '../api/heatmap'

/** A phone on the board — the API shape, used directly. */
export type PhoneCell = HeatmapPhone

type RGB = [number, number, number]

// Dark slate every card sits on.
const CARD_BASE: RGB = [24, 29, 41]
// Heat ramps — dim → bright as the value climbs. Carried from the
// approved prototype. active green, trouble red, stale amber, idle muted.
const ACTIVE_LO: RGB = [31, 122, 77]
const ACTIVE_HI: RGB = [74, 222, 128]
const TROUBLE_LO: RGB = [127, 29, 29]
const TROUBLE_HI: RGB = [239, 68, 68]
const STALE: RGB = [245, 158, 11]
const IDLE: RGB = [122, 134, 156]

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x)

function mix(a: RGB, b: RGB, t: number): RGB {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ]
}

const rgb = (c: RGB): string => `rgb(${c[0]}, ${c[1]}, ${c[2]})`

function accentRgb(cell: PhoneCell, maxDuration: number, maxScore: number): RGB {
  if (cell.state === 'idle') return IDLE
  if (cell.state === 'active') {
    if (cell.stale) return STALE
    return mix(ACTIVE_LO, ACTIVE_HI, clamp01((cell.in_call_seconds ?? 0) / maxDuration))
  }
  return mix(TROUBLE_LO, TROUBLE_HI, clamp01((cell.trouble_score ?? 0) / maxScore))
}

/** The accent (text/icon colour), card background and border for a cell.
 * The heat is the body tint + border + value colour — no accent bar. */
export interface CardColors {
  accent: string
  bg: string
  border: string
}

export function cardColors(
  cell: PhoneCell,
  maxDuration: number,
  maxScore: number,
): CardColors {
  const a = accentRgb(cell, maxDuration, maxScore)
  if (cell.state === 'idle') {
    return {
      accent: rgb(IDLE),
      bg: rgb(mix(CARD_BASE, IDLE, 0.06)),
      border: 'rgba(122, 134, 156, 0.14)',
    }
  }
  return {
    accent: rgb(a),
    bg: rgb(mix(CARD_BASE, a, 0.22)),
    border: rgb(mix(CARD_BASE, a, 0.46)),
  }
}

/** Dataset maxima the ramps scale against — `>= 1` so a single cell
 * doesn't divide by zero. */
export function gridMaxes(cells: PhoneCell[]): {
  maxDuration: number
  maxScore: number
} {
  let maxDuration = 1
  let maxScore = 1
  for (const c of cells) {
    if (c.state === 'active')
      maxDuration = Math.max(maxDuration, c.in_call_seconds ?? 0)
    else if (c.state === 'trouble')
      maxScore = Math.max(maxScore, c.trouble_score ?? 0)
  }
  return { maxDuration, maxScore }
}

/** Sort the fleet by name — numeric-aware so "BG-2" precedes "BG-10".
 * Stable order = a fixed grid slot per phone, the whole point of the
 * board: starting/ending a call never reshuffles anything. */
export function buildCells(phones: HeatmapPhone[]): PhoneCell[] {
  return [...phones].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }),
  )
}

/** Indicator: phones in a call right now. */
export function countInCall(phones: HeatmapPhone[]): number {
  return phones.filter((p) => p.state === 'active').length
}

/** Indicator: phones whose current call has run longer than a minute. */
export function countOverMinute(phones: HeatmapPhone[]): number {
  return phones.filter(
    (p) => p.state === 'active' && (p.in_call_seconds ?? 0) >= 60,
  ).length
}

/** Format a call duration: `M:SS` under an hour, `H:MM:SS` past it. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const hrs = Math.floor(s / 3600)
  const mins = Math.floor((s % 3600) / 60)
  const secs = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`
}

/** Short uppercase token for a trouble reason — replaces truncated prose
 * on the card. The icon (TroubleIcon / SignalBars / BatteryGauge) carries
 * the rest of the meaning; full text stays in the card tooltip. */
export function troubleToken(reason: HeatmapReason): string {
  switch (reason.type) {
    case 'offline':
      return 'ОФФЛАЙН'
    case 'no_sim':
      return 'НЕТ SIM'
    case 'low_battery':
      return 'ЗАРЯД'
    case 'weak_signal':
      return 'СИГНАЛ'
    case 'failed_calls':
      return `${reason.value ?? ''}✕`
    case 'stale':
      return 'ТИШИНА'
    case 'no_perm':
      return 'ДОСТУП'
  }
}

/** Layout for the fixed grid: pick `{cols, size}` so every one of `n`
 * square cards fits the W×H board area. `MIN_READABLE` is the floor —
 * below ~72px a card can't show "BG-NN" + a value legibly. For a normal
 * fleet on a laptop the natural size is well above it (no scroll); for a
 * huge fleet `size` clamps to the floor and the board area scrolls (the
 * page never does). Cap at 260 so a tiny fleet doesn't get giant cards. */
const MIN_READABLE = 72
const MAX_SIZE = 260

export function computeLayout(
  W: number,
  H: number,
  gap: number,
  n: number,
): { cols: number; size: number } {
  if (n <= 0 || W <= 0 || H <= 0) return { cols: 1, size: 0 }
  let best = { cols: 1, size: 0 }
  for (let cols = 1; cols <= n; cols++) {
    const rows = Math.ceil(n / cols)
    const s = Math.min(
      (W - gap * (cols - 1)) / cols,
      (H - gap * (rows - 1)) / rows,
    )
    if (s > best.size) best = { cols, size: s }
  }
  return {
    cols: best.cols,
    size: Math.floor(Math.max(MIN_READABLE, Math.min(MAX_SIZE, best.size))),
  }
}
